import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../config/Api";
import { HomeBanner } from "../../types";

const ADMIN_API = "/api/admin";

const getAuthHeaders = () => {
  const token = localStorage.getItem("jwt");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// --- BANNER ACTIONS ---

export const fetchAllBanners = createAsyncThunk<HomeBanner[]>(
  "adminBanner/fetchAllBanners",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_API}/banners`, getAuthHeaders());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch banners"
      );
    }
  }
);

export const createBanner = createAsyncThunk<HomeBanner, HomeBanner>(
  "adminBanner/createBanner",
  async (bannerData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ADMIN_API}/banners`,
        bannerData,
        getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create banner"
      );
    }
  }
);

export const toggleBannerStatus = createAsyncThunk<HomeBanner, number>(
  "adminBanner/toggleBannerStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `${ADMIN_API}/banners/${id}/toggle-active`,
        {},
        getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update banner status"
      );
    }
  }
);

export const deleteBanner = createAsyncThunk<number, number>(
  "adminBanner/deleteBanner",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${ADMIN_API}/banners/${id}`, getAuthHeaders());
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete banner"
      );
    }
  }
);

// --- SPONSORED PRODUCTS ACTIONS ---

export const fetchSponsoredProducts = createAsyncThunk<any[]>(
  "adminBanner/fetchSponsoredProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ADMIN_API}/sponsored-products`, getAuthHeaders());
      const data = response.data;

      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.content)) {
        return data.content;
      } else if (data && Array.isArray(data.sponsoredProducts)) {
        return data.sponsoredProducts;
      }

      return [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sponsored products"
      );
    }
  }
);

export const addSponsoredProduct = createAsyncThunk<
  any,
  { productId: number; validTill: string }
>(
  "adminBanner/addSponsoredProduct",
  async ({ productId, validTill }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ADMIN_API}/sponsored-products`,
        { productId, validTill },
        getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to sponsor product"
      );
    }
  }
);

export const toggleProductSponsorship = createAsyncThunk<any, number>(
  "adminBanner/toggleProductSponsorship",
  async (sponsoredRecordId, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `${ADMIN_API}/sponsored-products/${sponsoredRecordId}/toggle`,
        {},
        getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle sponsorship"
      );
    }
  }
);

export const deleteSponsoredProduct = createAsyncThunk<number, number>(
  "adminBanner/deleteSponsoredProduct",
  async (sponsoredRecordId, { rejectWithValue }) => {
    try {
      await api.delete(
        `${ADMIN_API}/sponsored-products/${sponsoredRecordId}`,
        getAuthHeaders()
      );
      return sponsoredRecordId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete sponsored product"
      );
    }
  }
);

// --- SLICE INTERFACE & STATE ---

interface AdminBannerState {
  banners: HomeBanner[];
  sponsoredProducts: any[];
  loading: boolean;
  error: string | null;
}

const initialState: AdminBannerState = {
  banners: [],
  sponsoredProducts: [],
  loading: false,
  error: null,
};

const isSameProduct = (item: any, targetId: number) => {
  if (!item || !targetId) return false;
  const itemId = item.id ?? item.productId ?? item.product?.id;
  return Number(itemId) === Number(targetId);
};

const adminBannerSlice = createSlice({
  name: "adminBanner",
  initialState,
  reducers: {
    clearBannerError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload;
      })
      .addCase(fetchAllBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchSponsoredProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSponsoredProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.sponsoredProducts = action.payload;
      })
      .addCase(fetchSponsoredProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addSponsoredProduct.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload.product?.id;
        const existsIndex = state.sponsoredProducts.findIndex((p) =>
          isSameProduct(p, payloadId)
        );

        if (existsIndex !== -1) {
          state.sponsoredProducts[existsIndex] = action.payload;
        } else {
          state.sponsoredProducts.unshift(action.payload);
        }
      })

      .addCase(toggleProductSponsorship.fulfilled, (state, action) => {
        const payloadId = action.payload.id || action.payload.product?.id;
        const index = state.sponsoredProducts.findIndex((p) =>
          isSameProduct(p, payloadId)
        );

        if (index !== -1) {
          state.sponsoredProducts[index] = action.payload;
        }
      })

      .addCase(deleteSponsoredProduct.fulfilled, (state, action) => {
        const targetId = action.payload;
        state.sponsoredProducts = state.sponsoredProducts.filter(
          (p) => !isSameProduct(p, targetId)
        );
      });
  },
});

export const { clearBannerError } = adminBannerSlice.actions;
export default adminBannerSlice.reducer;